import { api } from "@/lib/api/api";
import { isValidEmail } from "@/lib/validation/email";
import { IGenericOption } from "@gettactic/api";
import { useUserList } from "@gettactic/hooks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OptionProps, Props, SelectInstance } from "react-select";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";
import { CustomAsyncSelect } from "../../CustomAsyncSelect";
import { Dict } from "@gettactic/helpers/src/types/common";
import {
    components,
    type ValueContainerProps,
    type GroupBase
} from "react-select";
import { UserLabel } from "@v2/components/labels/UserLabel/UserLabel";

const AlwaysShowPlaceholderValueContainer = <
    Option,
    IsMulti extends boolean,
    Group extends GroupBase<Option>
>(
    props: ValueContainerProps<Option, IsMulti, Group>
) => {
    const { children, selectProps } = props;

    const input = Array.isArray(children)
        ? children[children.length - 1]
        : children;

    const hasSearchQuery =
        typeof selectProps.inputValue === "string" &&
        selectProps.inputValue.trim().length > 0;

    return (
        <components.ValueContainer {...props}>
            {!hasSearchQuery && (
                <components.Placeholder {...(props as any)}>
                    {selectProps.placeholder}
                </components.Placeholder>
            )}
            {input}
        </components.ValueContainer>
    );
};

type PropsSelectUsers = Omit<
    Props<SelectInstance<{ label: string; value: string; picture?: string }>>,
    "onChange" | "isMulti" | "defaultValue"
> & {
    classNamePrefix: string | undefined;
    defaultValue?: string[] | undefined;
    setInitial?: (vals: IGenericOption[]) => void;
    isMulti?: boolean;
    filterValues?: (
        value: IGenericOption,
        index: number,
        array: IGenericOption[]
    ) => boolean;
    forceInclude?: string[];
    onChange: (arg: IGenericOption[] | null) => void;
    readonly?: boolean;
    managed: boolean;
};

type UserOptionData = IGenericOption & {
    profile_photo_url?: string;
    email?: string;
};

const UserOption = <
    Option extends UserOptionData,
    IsMulti extends boolean,
    Group extends GroupBase<Option>
>(
    props: OptionProps<Option, IsMulti, Group>
) => {
    const { data } = props;

    return (
        <components.Option {...props}>
            <UserLabel
                user={{ ...data, name: data.label }}
                showTitle={false}
                showEmail
                variant="dropdown"
            />
        </components.Option>
    );
};

export default function SelectUsersAsync({
                                             defaultValue,
                                             setInitial,
                                             filterValues,
                                             forceInclude = undefined,
                                             isMulti = true,
                                             readonly = false,
                                             managed = false,
                                             ...rest
                                         }: PropsSelectUsers) {
    const initLoading = useRef(true);
    const initFetching = useRef(false);
    const [initialList, setInitialList] = useState<UserOptionData[]>([]);
    const stableDefValue = useMemo(() => defaultValue, [defaultValue?.join("")]);

    useEffect(() => {
        const lookupUsers = [...(stableDefValue ?? []), ...(forceInclude ?? [])];
        const results = api.client.organizations.usersById(lookupUsers);
        results.then((res) => {
            initFetching.current = true;

            setInitialList(
                res
                    .filter((x) => !!x)
                    .map(
                        (user) =>
                            ({
                                value: user?.id ?? "",
                                label: user?.name ?? "",
                                profile_photo_url: user?.profile_photo_url ?? "",
                                email: user?.email
                            }) as UserOptionData
                    )
            );
        });
    }, [stableDefValue, forceInclude]);

    useEffect(() => {
        if (initLoading.current && initFetching.current) {
            const filterInitial = filterValues
                ? initialList.filter(filterValues)
                : initialList;
            if (setInitial) {
                const newInitial = filterInitial.filter((x) =>
                    defaultValue?.includes(x.value)
                );
                setInitial(newInitial);
            }

            initLoading.current = false;
        }
    }, [initialList, filterValues, defaultValue, setInitial]);

    const [query, setQuery] = useState("");
    const userList = useUserList(query, null, null, managed);
    const loadOptionsRef = useRef<any>(null);

    useEffect(() => {
        if (loadOptionsRef.current && userList.data?.options) {
            const options: UserOptionData[] = (
                filterValues
                    ? userList.data.options.filter(filterValues)
                    : userList.data.options
            ).map((option: any) => {
                const full = userList.data?.byId[option.value];
                return {
                    ...option,
                    profile_photo_url: full?.profile_photo_url ?? "",
                    email: full?.email
                } as UserOptionData;
            });

            loadOptionsRef.current(options);
        }
    }, [userList, filterValues]);

    const loadOptions = useCallback((inputValue: string, callback: any) => {
        loadOptionsRef.current = callback;
        setQuery(inputValue);
    }, []);

    const isMobileApp = isTacticAppWebView();

    return (
        <CustomAsyncSelect
            {...(rest as unknown as Dict)}
            menuPosition="fixed"
            menuPortalTarget={
                typeof document !== "undefined" ? document.body : undefined
            }
            key={`${initialList.map((x) => x.value).join("")}`}
            loadOptions={loadOptions}
            defaultValue={initialList.filter((x) => defaultValue?.includes(x.value))}
            defaultOptions
            isMulti={isMulti}
            isSearchable={!readonly}
            menuIsOpen={readonly ? false : undefined}
            allowCreateWhileLoading={false}
            formatCreateLabel={(inputValue) => `Add: ${inputValue}`}
            isValidNewOption={(inputValue) =>
                inputValue.length > 0 && isValidEmail(inputValue)
            }
            noOptionsMessage={() =>
                "Try a different search term or add users by email"
            }
            components={
                isMulti
                    ? {
                        ValueContainer: AlwaysShowPlaceholderValueContainer,
                        MultiValue: () => null,
                        MultiValueContainer: () => null,
                        Option: UserOption,
                        IndicatorSeparator: () => null,
                        ...(rest.components as any)
                    }
                    : {
                        Option: UserOption,
                        IndicatorSeparator: () => null,
                        ...(rest.components as any)
                    }
            }
        />
    );
}
